// The report-currency filter (SM-498) — "narrow this ledger to one
// currency", for the financial tables whose rows have already been
// booked and each carry a currency of their own.
//
// Deliberately NOT <StakeCurrencyFilter> (SM-490), and the difference is
// the whole point of having two. That one asks what exposure was PAID
// in, so its list is the two debit legs and GREEN is absent because no
// position is ever opened in it. This one narrows money that has
// already moved, where all three are reachable: a CASH fee is collected
// in GOLD, a GAME buy-side fee in RED and a GAME sell-side fee in GREEN,
// and a commission is a slice of the fee it was carved from, in that
// same currency. Sharing one control would have meant one of the two
// pages offering an option that can only ever return an empty page.
//
// Parsing is fail-OPEN and the server is fail-CLOSED, on purpose. A URL
// value this cannot read becomes "no filter" so the picker and the table
// never contradict each other; settlement answers an unknown code with
// nothing rather than with everything, for callers that are not a
// picker.
import { Select } from "@/components/ui/Select";
import { CurrencyMonogram } from "@/components/ui/CurrencyAmount";
import { useCurrencySettings } from "@/components/currency/CurrencyPresentationProvider";
import {
  CURRENCY_CODES,
  isCurrencyCode,
  resolveCurrencyPresentation,
  type CurrencyCode,
} from "@/currency";

/**
 * Read a currency filter out of untrusted text (a URL search param).
 *
 * Anything outside the fixed set — a typo, a code from another product,
 * an alias the API accepts but the picker cannot show — reads as NO
 * filter. Leaving the table filtered while the control says "All
 * currencies" is the failure this avoids.
 */
export function parseCurrencyFilter(raw: unknown): CurrencyCode | undefined {
  return isCurrencyCode(raw) ? raw : undefined;
}

/**
 * The picker. Options carry each currency's CONFIGURED name and mark —
 * whatever an operator set on /currencies — so the list reads the same
 * as the amounts in the table below it rather than as three wire codes.
 */
export function CurrencyFilter({
  value,
  onChange,
  label = "Currency",
  hideLabel = false,
  placeholder = "All currencies",
  className,
}: {
  value: CurrencyCode | undefined;
  onChange: (next: CurrencyCode | undefined) => void;
  label?: string;
  /**
   * Drop the stacked label where it will not fit — a card header whose
   * other controls sit on one line. The label still NAMES the control,
   * it just names it to assistive technology instead of on screen.
   */
  hideLabel?: boolean;
  placeholder?: string;
  className?: string;
}) {
  const settings = useCurrencySettings();
  return (
    <Select
      className={className}
      label={hideLabel ? undefined : label}
      aria-label={label}
      placeholder={placeholder}
      value={value ?? ""}
      onChange={(e) => onChange(parseCurrencyFilter(e.target.value))}
      options={CURRENCY_CODES.map((code) => ({
        value: code,
        // Name AND code: the name is what the console shows everywhere
        // else, the code is what a support ticket or an API call says.
        label: `${resolveCurrencyPresentation(code, settings).displayName} (${code})`,
        icon: <CurrencyMonogram currency={code} />,
      }))}
    />
  );
}
